import React, { useEffect, useRef, useState } from "react";
import API from "../../services/api";
import { formatLocalDateTime } from "../../utils/dateTime";
import { localExpiry } from "../../utils/accessManagement";
import {
    ON_CHANGE_INTERVAL_PRESETS,
    ON_CHANGE_INTERVAL_SECONDS,
    OPC_DATA_TYPES,
    RECORDING_MODES,
    editorPatchPayload,
    intervalLabel,
} from "./tagCreation";

const fieldMessage = (errors, key) => errors[key] || "";

const EMPTY = {
    name: "",
    access_mode: "",
    signal_type: "",
    recording_mode: "",
    on_change_interval_seconds: "300",
    formula: "",
    decimal_places: "",
    group: "",
    description: "",
    opc_data_type: "",
    is_calculated: false,
    expires_at: "",
};

const formSnapshot = (value) => JSON.stringify(value);

const TagEditor = ({ tagId, serverName, onChanged, onDirtyChange }) => {
    const baselineRef = useRef(formSnapshot(EMPTY));
    const dirtyNotifyRef = useRef(onDirtyChange);
    dirtyNotifyRef.current = onDirtyChange;
    const [detail, setDetail] = useState(null);
    const [groups, setGroups] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState("");
    const [sample, setSample] = useState("1");
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        let active = true;
        if (!tagId) {
            setDetail(null);
            return undefined;
        }
        const load = async () => {
            setLoading(true);
            setError("");
            setMessage("");
            try {
                const [tagResponse, groupResponse] = await Promise.all([
                    API.get(`/tags/${tagId}/`),
                    API.get("/tag-groups/"),
                ]);
                if (!active) return;
                setDetail(tagResponse.data);
                setGroups(Array.isArray(groupResponse.data) ? groupResponse.data : []);
                const nextForm = {
                    name: tagResponse.data.name || "",
                    access_mode: tagResponse.data.access_mode || "",
                    signal_type: tagResponse.data.signal_type || "",
                    recording_mode: tagResponse.data.recording_mode || "",
                    on_change_interval_seconds: String(
                        tagResponse.data.on_change_interval_seconds
                        ?? ((tagResponse.data.on_change_interval_minutes ?? 5) * 60)
                    ),
                    formula: tagResponse.data.formula || "",
                    decimal_places: tagResponse.data.decimal_places ?? "",
                    group: tagResponse.data.group || "",
                    description: tagResponse.data.description || "",
                    opc_data_type: tagResponse.data.opc_data_type || "",
                    is_calculated: Boolean(tagResponse.data.is_calculated),
                    expires_at: localExpiry(tagResponse.data.expires_at),
                };
                baselineRef.current = formSnapshot(nextForm);
                setFieldErrors({});
                setForm(nextForm);
            } catch (loadError) {
                if (!active) return;
                setDetail(null);
                const detailText = loadError?.response?.data?.detail;
                setError(detailText || "Tag configuration is not available for this session yet.");
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [tagId]);

    useEffect(() => {
        dirtyNotifyRef.current?.(Boolean(detail) && formSnapshot(form) !== baselineRef.current);
    }, [detail, form]);

    const update = (key, value) => setForm((current) => {
        const next = { ...current, [key]: value };
        if (key === "signal_type" && value === "DIGITAL") {
            next.decimal_places = 0;
        }
        return next;
    });

    const validate = () => {
        const nextErrors = {};
        const recording = form.recording_mode || detail?.effective?.recording_mode || "ALWAYS";
        if (recording === "ON_CHANGE") {
            const seconds = Number(form.on_change_interval_seconds);
            if (!ON_CHANGE_INTERVAL_SECONDS.includes(seconds)) {
                nextErrors.on_change_interval_seconds = "Choose 5, 10, 20, 30, 40 or 50 seconds, or 1 to 60 minutes.";
            }
        }
        if (form.signal_type === "ANALOG" && form.decimal_places !== "" && form.decimal_places != null) {
            const places = Number(form.decimal_places);
            if (!Number.isInteger(places) || places < 0 || places > 12) {
                nextErrors.decimal_places = "Decimal places must be a whole number from 0 to 12.";
            }
        }
        if (form.is_calculated && form.signal_type !== "ANALOG") {
            nextErrors.is_calculated = "Calculated tags must be analog and read only.";
        }
        if (form.is_calculated && !String(form.formula || "").trim()) {
            nextErrors.formula = "Calculated tags require a formula.";
        }
        return nextErrors;
    };

    const save = async () => {
        const nextErrors = validate();
        setFieldErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            setError("Correct the highlighted fields before saving.");
            return;
        }
        setSaving(true);
        setError("");
        setMessage("");
        try {
            const expiry = form.expires_at ? new Date(form.expires_at) : null;
            if (expiry && Number.isNaN(expiry.getTime())) {
                setFieldErrors({ expires_at: "Enter a valid expiry date and time." });
                setError("Correct the highlighted fields before saving.");
                return;
            }
            const payload = {
                ...editorPatchPayload(form),
                expires_at: expiry ? expiry.toISOString() : null,
            };
            const response = await API.patch(`/tags/${tagId}/`, payload);
            setDetail(response.data);
            const savedForm = {
                ...form,
                recording_mode: response.data.recording_mode || "",
                on_change_interval_seconds: String(
                    response.data.on_change_interval_seconds
                    ?? form.on_change_interval_seconds
                ),
                decimal_places: response.data.decimal_places ?? "",
                access_mode: response.data.access_mode || "",
                is_calculated: Boolean(response.data.is_calculated),
                formula: response.data.formula || "",
                expires_at: localExpiry(response.data.expires_at),
            };
            baselineRef.current = formSnapshot(savedForm);
            setForm(savedForm);
            setFieldErrors({});
            setMessage("Tag configuration saved.");
            onChanged?.();
        } catch (saveError) {
            const data = saveError?.response?.data || {};
            setFieldErrors(data.fields || {});
            setError(data.detail || "The tag could not be saved.");
        } finally {
            setSaving(false);
        }
    };

    const restore = async () => {
        setSaving(true);
        setError("");
        try {
            const response = await API.post(`/tags/${tagId}/restore/`);
            setDetail(response.data);
            setMessage("Tag restored. Polling resumes on the next collector cycle.");
            onChanged?.();
        } catch (restoreError) {
            const data = restoreError?.response?.data || {};
            setFieldErrors(data.fields || {});
            setError(data.detail || "The tag could not be restored.");
        } finally {
            setSaving(false);
        }
    };

    const archive = async () => {
        const confirmed = window.confirm(
            "Archive this tag? History, designer objects, and section links stay. Polling and writes stop until it is restored."
        );
        if (!confirmed) return;
        setSaving(true);
        setError("");
        try {
            await API.post(`/tags/${tagId}/archive/`);
            setMessage("Tag archived. Its history is still stored.");
            onChanged?.();
        } catch (archiveError) {
            setError(archiveError?.response?.data?.detail || "The tag could not be archived.");
        } finally {
            setSaving(false);
        }
    };

    const runPreview = async () => {
        setError("");
        try {
            const response = await API.post("/tags/formula-preview/", {
                formula: form.formula,
                sample: form.signal_type === "DIGITAL" ? sample === "1" || sample === "true" : Number(sample),
                signal_type: form.signal_type || null,
                decimal_places: form.decimal_places === "" ? null : Number(form.decimal_places),
            });
            setPreview(`${response.data.raw_value} -> ${response.data.processed_value}`);
        } catch (previewError) {
            setPreview("");
            setError(previewError?.response?.data?.detail || "Formula preview failed.");
        }
    };

    if (!tagId) return null;
    if (loading) return <p className="tag-page__hint">Loading tag configuration…</p>;
    if (!detail) return <p className="tag-page__hint">{error}</p>;

    const effective = detail.effective || {};
    const status = detail.connection_status || "UNKNOWN";
    const lifecycle = detail.is_active === false ? "ARCHIVED" : "ACTIVE";
    const kind = detail.is_calculated ? "CALCULATED" : "PHYSICAL";
    const serverLabel = serverName || detail.server_name || detail.server || "—";
    const dirty = formSnapshot(form) !== baselineRef.current;
    return (
        <div className="tag-page__editor">
            <dl className="tag-page__identity">
                <div>
                    <dt>Name</dt>
                    <dd>{detail.name || "—"}</dd>
                </div>
                <div>
                    <dt>OPC Server</dt>
                    <dd>{serverLabel}</dd>
                </div>
                <div>
                    <dt>Node ID</dt>
                    <dd className="tag-page__mono">{detail.node_id || "—"}</dd>
                </div>
                <div>
                    <dt>Description</dt>
                    <dd>{detail.description || "—"}</dd>
                </div>
                <div>
                    <dt>Lifecycle</dt>
                    <dd><span className={`tag-page__lifecycle tag-page__lifecycle--${lifecycle}`}>{lifecycle}</span></dd>
                </div>
                <div>
                    <dt>Tag type</dt>
                    <dd>{kind}</dd>
                </div>
                <div>
                    <dt>OPC data type</dt>
                    <dd className="tag-page__mono">{detail.opc_data_type || "Not set"}</dd>
                </div>
            </dl>
            <div className="tag-page__editor-grid">
                <section className="tag-page__group">
                    <h3>Identity</h3>
                    <label className="tag-page__field">
                        <span>Tag Name</span>
                        <input value={form.name} onChange={(event) => update("name", event.target.value)} />
                        {fieldMessage(fieldErrors, "name") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "name")}</p> : null}
                    </label>
                    <label className="tag-page__field">
                        <span>OPC Server</span>
                        <input value={serverLabel} disabled />
                    </label>
                    <label className="tag-page__field">
                        <span>Node ID</span>
                        <input className="tag-page__mono" value={detail.node_id || ""} disabled />
                    </label>
                    <label className="tag-page__field">
                        <span>Description</span>
                        <textarea rows="3" value={form.description} onChange={(event) => update("description", event.target.value)} />
                        {fieldMessage(fieldErrors, "description") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "description")}</p> : null}
                    </label>
                </section>
                <section className="tag-page__group">
                    <h3>Signal / OPC Data Type</h3>
                    <label className="tag-page__field">
                        <span>Signal type</span>
                        {form.signal_type === "STRING" ? (
                            <input value="String" disabled />
                        ) : (
                            <select value={form.signal_type} onChange={(event) => update("signal_type", event.target.value)}>
                                <option value="">Unspecified</option>
                                <option value="ANALOG">Analog</option>
                                <option value="DIGITAL">Digital</option>
                            </select>
                        )}
                        {fieldMessage(fieldErrors, "signal_type") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "signal_type")}</p> : null}
                    </label>
                    <label className="tag-page__field">
                        <span>OPC data type</span>
                        {form.opc_data_type === "String" || form.signal_type === "STRING" ? (
                            <input value="String" disabled />
                        ) : (
                            <select value={form.opc_data_type} onChange={(event) => update("opc_data_type", event.target.value)}>
                                <option value="">Not set</option>
                                {OPC_DATA_TYPES.filter((typeName) => typeName !== "String").map((typeName) => (
                                    <option key={typeName} value={typeName}>{typeName}</option>
                                ))}
                            </select>
                        )}
                        {fieldMessage(fieldErrors, "opc_data_type") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "opc_data_type")}</p> : null}
                    </label>
                    <p className="tag-page__hint">Changing between numeric and String types is blocked. Existing history is not reinterpreted.</p>
                </section>
                <section className="tag-page__group">
                    <h3>Recording Settings</h3>
                    <label className="tag-page__field">
                        <span>Recording Mode</span>
                        <select value={form.recording_mode} onChange={(event) => update("recording_mode", event.target.value)}>
                            <option value="">Inherit group default</option>
                            {RECORDING_MODES.map((mode) => (
                                <option key={mode.value} value={mode.value}>{mode.label}</option>
                            ))}
                        </select>
                        {fieldMessage(fieldErrors, "recording_mode") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "recording_mode")}</p> : null}
                    </label>
                    {(form.recording_mode || detail?.effective?.recording_mode || "ALWAYS") === "ON_CHANGE" ? (
                        <label className="tag-page__field">
                            <span>On Change Interval</span>
                            <select
                                value={form.on_change_interval_seconds}
                                onChange={(event) => update("on_change_interval_seconds", event.target.value)}
                            >
                                {ON_CHANGE_INTERVAL_SECONDS.map((seconds) => (
                                    <option key={seconds} value={String(seconds)}>{intervalLabel(seconds)}</option>
                                ))}
                            </select>
                            {fieldMessage(fieldErrors, "on_change_interval_seconds") ? (
                                <p className="tag-page__field-error">{fieldMessage(fieldErrors, "on_change_interval_seconds")}</p>
                            ) : null}
                            <span className="tag-page__presets" role="group" aria-label="Common On Change intervals">
                                {ON_CHANGE_INTERVAL_PRESETS.map((seconds) => (
                                    <button
                                        key={seconds}
                                        type="button"
                                        className={`tag-page__preset${String(form.on_change_interval_seconds) === String(seconds) ? " is-selected" : ""}`}
                                        onClick={() => update("on_change_interval_seconds", String(seconds))}
                                    >
                                        {intervalLabel(seconds)}
                                    </button>
                                ))}
                            </span>
                        </label>
                    ) : null}
                    {form.signal_type === "STRING" ? (
                        <p className="tag-page__hint">Decimal places do not apply to string values.</p>
                    ) : (
                        <label className="tag-page__field">
                            <span>Decimal Places</span>
                            <input
                                type="number"
                                min="0"
                                max="12"
                                step="1"
                                disabled={form.signal_type === "DIGITAL"}
                                value={form.signal_type === "DIGITAL" ? 0 : form.decimal_places}
                                onChange={(event) => update("decimal_places", event.target.value)}
                            />
                            {fieldMessage(fieldErrors, "decimal_places") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "decimal_places")}</p> : null}
                        </label>
                    )}
                    <p className="tag-page__hint">These settings apply to future samples. Stored history stays as recorded.</p>
                </section>
                <section className="tag-page__group">
                    <h3>Access and Lifecycle</h3>
                    <label className="tag-page__field">
                        <span>Access mode</span>
                        <select
                            value={form.access_mode}
                            onChange={(event) => update("access_mode", event.target.value)}
                            disabled={Boolean(form.is_calculated)}
                        >
                            <option value="">Legacy / inherit</option>
                            <option value="READ_ONLY">Read only</option>
                            <option value="READ_WRITE">Read and write</option>
                        </select>
                        {fieldMessage(fieldErrors, "access_mode") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "access_mode")}</p> : null}
                    </label>
                    <label className="tag-page__field">
                        <span>Expiry (your local time)</span>
                        <input
                            type="datetime-local"
                            step="1"
                            value={form.expires_at}
                            onChange={(event) => update("expires_at", event.target.value)}
                        />
                        {fieldMessage(fieldErrors, "expires_at") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "expires_at")}</p> : null}
                    </label>
                    <p className="tag-page__hint">Expiry: {form.expires_at ? "Scheduled" : "Unlimited"}. An expired tag is archived automatically; stored history remains available.</p>
                    <p className="tag-page__hint">Lifecycle {lifecycle}. Archive keeps history until a confirmed deletion job runs.</p>
                </section>
                <section className="tag-page__group">
                    <h3>Group and Description</h3>
                    <label className="tag-page__field">
                        <span>Group</span>
                        <select value={form.group} onChange={(event) => update("group", event.target.value)}>
                            <option value="">No group</option>
                            {groups.map((group) => (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))}
                        </select>
                        {fieldMessage(fieldErrors, "group") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "group")}</p> : null}
                    </label>
                </section>
                <section className="tag-page__group tag-page__editor-span">
                    <h3>Formula</h3>
                    {form.signal_type === "STRING" ? (
                        <p className="tag-page__hint">Numeric formulas are not available for string tags.</p>
                    ) : (
                    <>
                    <label className="tag-page__check" htmlFor="tag-editor-calculated">
                        <input
                            id="tag-editor-calculated"
                            type="checkbox"
                            checked={Boolean(form.is_calculated)}
                            onChange={(event) => {
                                const checked = event.target.checked;
                                setForm((current) => ({
                                    ...current,
                                    is_calculated: checked,
                                    access_mode: checked ? "READ_ONLY" : current.access_mode,
                                }));
                            }}
                        />
                        Calculated tag
                    </label>
                    {fieldMessage(fieldErrors, "is_calculated") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "is_calculated")}</p> : null}
                    <p className="tag-page__hint">
                        Calculated tags are read only. They never write to the PLC. Changing a formula changes future readings only. Historical values stay as stored.
                    </p>
                    {form.is_calculated && !String(form.formula || "").trim() ? (
                        <p className="tag-page__hint">A formula is required while calculated mode is on.</p>
                    ) : null}
                    {!form.is_calculated && String(form.formula || "").trim() ? (
                        <p className="tag-page__hint">Clear the formula before switching this tag back to a physical tag.</p>
                    ) : null}
                    <label className="tag-page__field">
                        <span>Expression</span>
                        <input value={form.formula} onChange={(event) => update("formula", event.target.value)} placeholder="x * 0.1" />
                        {fieldMessage(fieldErrors, "formula") ? <p className="tag-page__field-error">{fieldMessage(fieldErrors, "formula")}</p> : null}
                    </label>
                    <div className="tag-page__actions">
                        <label className="tag-page__field">
                            <span>Sample value</span>
                            <input value={sample} onChange={(event) => setSample(event.target.value)} />
                        </label>
                        <button type="button" className="tag-page__button" onClick={runPreview}>Preview</button>
                    </div>
                    {preview ? <p className="tag-page__hint">Preview {preview}</p> : null}
                    </>
                    )}
                </section>
                <section className="tag-page__group">
                    <h3>Effective settings</h3>
                    <p className="tag-page__hint">Access {effective.access_mode || "legacy"}.</p>
                    <p className="tag-page__hint">Recording {effective.recording_mode || "ALWAYS"}.</p>
                    <p className="tag-page__hint">On Change interval {intervalLabel(detail.on_change_interval_seconds ?? form.on_change_interval_seconds)}.</p>
                    <p className="tag-page__hint">Decimals {form.signal_type === "STRING" ? "not applicable" : (effective.decimal_places ?? "full precision")}.</p>
                    <p className="tag-page__hint">
                        Last value {detail.value_kind === "string"
                            ? (detail.last_value_is_null ? "(null)" : (detail.last_value === "" ? "(empty)" : (detail.last_value ?? "—")))
                            : (detail.last_value ?? "—")}
                        .
                    </p>
                </section>
                <section className="tag-page__group">
                    <h3>Connection</h3>
                    <p className={`tag-page__status tag-page__status--${status}`}>{status}</p>
                    <p className="tag-page__hint">Last good read {formatLocalDateTime(detail.last_successful_read_at)}.</p>
                    <p className="tag-page__hint">Last status change {formatLocalDateTime(detail.last_status_change_at)}.</p>
                </section>
            </div>
            {error ? <p className="tag-page__banner is-error" role="alert">{error}</p> : null}
            {message ? <p className="tag-page__hint">{message}</p> : null}
            {dirty ? <p className="tag-page__hint">Unsaved changes stay in this editor when you close it.</p> : null}
            <div className="tag-page__actions tag-page__editor-actions">
                <button type="button" className="tag-page__button tag-page__button--primary" onClick={save} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                </button>
                {lifecycle === "ARCHIVED" ? (
                    <button type="button" className="tag-page__button" onClick={restore} disabled={saving}>
                        Restore
                    </button>
                ) : (
                    <button type="button" className="tag-page__button tag-page__button--danger" onClick={archive} disabled={saving}>
                        Archive
                    </button>
                )}
            </div>
        </div>
    );
};

export default TagEditor;
